import React from 'react';

import { usePermission } from '../../context/PermissionContext';
import {
  ADMISSION_STATUS_LABELS,
  CHARGE_PERMISSIONS,
  hasOpenDeathRecord,
  isAwaitingBed,
  isInDischargeFlow,
  isMoneyHidden,
} from '../../constants/admission';
import { describeBalance } from './bill/billModel';
import { describeDuesState } from './duesState';
import { plannedDischargeChipText } from './discharge/planDischargeModel';
import { treatingDoctorLabel } from './deskFilters';
import ViewerRelationshipChip from './ViewerRelationshipChip';

/**
 * AdmissionRailItem (UX-1 · Variant B) — one row in the always-visible left
 * rail (the searchable long list). Renders PURELY from the admission's
 * denormalized fields — ZERO per-row fetch — so the rail scrolls a 30+
 * admission list cheaply: name · IP · balance (owe/advance coloured) ·
 * status/bed/MLC/dues chips · doctor.
 *
 * The balance leg reads the SAME `describeBalance` selector the workspace KPI
 * strip and the Bill-tab totals read (UX-1 AC7 single source), and is gated on
 * VIEW_RUNNING_BILL + an IP number exactly as Variant A's desk row was — a
 * viewer without the bill slug never sees a balance leaked in the rail.
 * admission-access: `money_visible === false` (server-withheld money) drops
 * the balance, the deposit-low flag and the dues chip outright — never ₹0.
 * `undefined` (older server) keeps today's behaviour.
 *
 * Death-record flag (FE-4, D40 Variant A — "prominent" flag weight): a
 * red-tinted row plus the "Deceased · Pending discharge" chip, gated on the
 * SAME `hasOpenDeathRecord(admission)` derived source every other flag
 * surface reads (S1) — never re-derived inline. Without this the desk list
 * shows a recorded death as an ordinary admitted row, invisible to anyone not
 * already inside that patient's workspace.
 *
 * S4 (build-plan FE-3, owner ruling 5) — planned-discharge chip, read-only,
 * text from the ONE `plannedDischargeChipText` selector (FE-2's
 * `planDischargeModel.js`, reused verbatim by the workspace header/FE-4's
 * running-bill chip). Renders off `admission.planned_discharge_at` already on
 * the loaded row — zero extra fetch. Absent (not an empty chip) when unset,
 * unparseable, OR once the stay is discharged/terminal (D3) — the selector
 * returns `''` in every one of those cases, so the truthiness check IS the
 * "no empty chip" guard.
 *
 * admission-access M-FE-2 (S16-AC7, S17) — a "Primary" / "Added" tag after the
 * name when the server says the VIEWER is on this admission's care team
 * (`viewer_relationship`), and the doctor line names the first three treating
 * doctors then "+n" (`treatingDoctorLabel`, the one selector the workspace
 * header and the IPD page row share). No added doctors ⇒ today's line exactly.
 */
const chip = 'text-[10.5px] font-semibold px-1.5 py-0.5 rounded-full border whitespace-nowrap';

function Chip({ text, tone }) {
  return <span className={`${chip} ${tone}`}>{text}</span>;
}

/**
 * roomBedDisplay — the room/bed label shown in BOTH the Chip and the
 * subtitle (single helper, owner ruling — DESK-IP-SORT scope addition, refined
 * 2026-08-12). Backend `AdmissionResponse` carries two additive, nullable
 * fields (`room_label`, `room_is_bay`; null on legacy rows stamped before
 * this change):
 *   - room_is_bay === false && room_label → "Room {room_label} · {bed_label}"
 *     (room number FIRST, bed number alongside); "Room {room_label}" alone
 *     when bed_label is missing.
 *   - room_is_bay === true                → "{ward_name} · {bed_label}"
 *     (a ward/bay bed — the bed number is the meaningful identifier).
 *   - room info null/absent (legacy)      → UNCHANGED fallback: bed_label,
 *     else ward_name, else null.
 */
function roomBedDisplay(a) {
  if (a.room_is_bay === false && a.room_label) {
    return a.bed_label ? `Room ${a.room_label} · ${a.bed_label}` : `Room ${a.room_label}`;
  }
  if (a.room_is_bay === true) return [a.ward_name, a.bed_label].filter(Boolean).join(' · ') || null;
  return a.bed_label || a.ward_name || null;
}

export default function AdmissionRailItem({ admission, active, onSelect }) {
  const a = admission;
  const canViewBill = usePermission(CHARGE_PERMISSIONS.VIEW_RUNNING_BILL);
  const moneyHidden = isMoneyHidden(a);
  const showBalance = canViewBill && !moneyHidden && !!a.ip_number && a.balance_paise != null;
  const bal = showBalance ? describeBalance(a.balance_paise) : null;
  const noBed = !a.current_bed_id;
  const awaiting = isAwaitingBed(a);
  const dues = describeDuesState(a);
  const deceasedOpen = hasOpenDeathRecord(a);
  const plannedDischargeText = plannedDischargeChipText(a);

  return (
    <button
      type="button"
      onClick={() => onSelect(a.admission_id)}
      data-testid={`rail-item-${a.admission_id}`}
      aria-current={active ? 'true' : undefined}
      className={`w-full text-left px-3.5 py-2.5 border-b border-slate-200 dark:border-slate-700 focus:outline-none ${
        deceasedOpen
          ? 'bg-red-50 dark:bg-red-900/20 border-l-[3px] border-l-red-500 pl-3'
          : active
            ? 'bg-teal-50 dark:bg-teal-900/30 border-l-[3px] border-l-teal-500 pl-3'
            : 'bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700/60'
      }`}
    >
      <div className="flex items-center gap-2">
        <span className="font-semibold text-[13px] text-slate-900 dark:text-white truncate">{a.patient_name || a.patient_id}</span>
        <ViewerRelationshipChip relationship={a.viewer_relationship} testId={`rail-viewer-tag-${a.admission_id}`} />
        {bal && (
          <span
            className={`ml-auto font-bold text-[12.5px] tabular-nums flex-none ${
              bal.kind === 'advance' ? 'text-teal-700 dark:text-teal-300' : bal.kind === 'owed' ? 'text-red-700 dark:text-red-300' : 'text-slate-600 dark:text-slate-300'
            }`}
            data-testid={`rail-balance-${a.admission_id}`}
          >
            {bal.text}
          </span>
        )}
      </div>
      <div className="flex items-center gap-1.5 flex-wrap mt-1">
        <span className="text-[11px] text-slate-500 dark:text-slate-400">{a.ip_number}</span>
        <Chip
          text={ADMISSION_STATUS_LABELS[a.status] || a.status}
          tone={isInDischargeFlow(a.status)
            ? 'border-teal-400 bg-teal-100 dark:bg-teal-900/40 text-teal-800 dark:text-teal-200'
            : 'border-slate-300 dark:border-slate-600 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200'}
        />
        {a.is_mlc && <Chip text="MLC" tone="border-amber-400 bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200" />}
        {!noBed
          ? <Chip text={roomBedDisplay(a) || 'Bed'} tone="border-teal-400 bg-teal-100 dark:bg-teal-900/40 text-teal-800 dark:text-teal-200" />
          : awaiting
            ? <Chip text="Awaiting bed" tone="border-amber-400 bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200" />
            : null}
        {!moneyHidden && a.deposit_low && (
          <span className={`${chip} border-amber-400 bg-amber-100 dark:bg-amber-900/40 text-amber-800 dark:text-amber-200`} data-testid={`rail-deposit-low-${a.admission_id}`}>⚠</span>
        )}
        {!moneyHidden && dues.kind !== 'NONE' && <Chip text={dues.chipLabel} tone={dues.chipTone} />}
        {plannedDischargeText && (
          <span data-testid={`rail-planned-discharge-${a.admission_id}`}>
            <Chip text={plannedDischargeText} tone="border-indigo-400 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-800 dark:text-indigo-200" />
          </span>
        )}
        {/* [NEEDS-PO] copy — death-record slice, mockup Variant A state 3 (same chip copy as the workspace banner). */}
        {deceasedOpen && (
          <span data-testid={`rail-deceased-${a.admission_id}`}>
            <Chip text="● Deceased · Pending discharge" tone="border-red-400 bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200" />
          </span>
        )}
      </div>
      <div className="text-[11.5px] text-slate-500 dark:text-slate-400 mt-1 break-words" data-testid={`rail-doctors-${a.admission_id}`}>
        {treatingDoctorLabel(a)} · {roomBedDisplay(a) || 'No bed'}
      </div>
    </button>
  );
}
